import * as React from 'react';
import { OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import { useServicePageProps } from '../hooks';
import { useUpsaleWarning } from '../../../Checkout/components/Checkout/Upsale/hooks';
import { ServiceType } from '../../../Checkout/components/Checkout/OrderPaidServiceCards/types';
import { useSelector } from 'react-redux';
import {
	getMealMinPrice,
	getMealServices,
	getServicesFallbackURL,
	shouldRedirectToFallback
} from '../../../Checkout/store/order/selectors';
import Warning from '../../../Checkout/components/Checkout/Upsale/Warning/Warning';
import ServicePage from '../../../Checkout/components/Checkout/ServicePage/ServicePage';
import ServiceCardDialog from '../../../Checkout/components/Checkout/OrderPaidServiceCards/components/ServiceCardDialog/ServiceCardDialog';
import { useTranslation } from 'react-i18next';
import { getMealsPrice } from '../../../Checkout/store/orderPrice/selectors';
import { useTheme } from '../../../theme';
import icons from '../../icons';
import { Money } from '../../../types';
import cn from 'classnames';
import { useRenderers } from '../../../renderProps';
import { ServiceButton } from '../../types';
import { useCart } from '../../../Checkout/components/Checkout/Stepbar/hooks';
import { useIsCanModifyServices } from '../../hooks';
import { ServiceSummary } from '../../../features/service-summary';

interface Props {
	aggregationMode?: boolean;
	segmentId?: string;
	minPrice?: Money;
	totalPrice?: Money;
	isSelected?: boolean;
	aggregationIcons?: React.ReactNode[];
	className?: string;
}

const MealAdditionalService: React.FC<Props> = ({
	aggregationMode,
	segmentId,
	minPrice: minPriceProps,
	totalPrice: totalPriceProps,
	isSelected: isSelectedProps,
	children,
	aggregationIcons,
	className
}) => {
	const servicePageProps = useServicePageProps(OrderAdditionalServiceGdsServiceServiceType.Meal);
	const servicesRedirectURL = useSelector(getServicesFallbackURL);
	const shouldRedirect = useSelector(shouldRedirectToFallback);
	const { showWarning, closeWarning, toggleWarningIfOrderConfirmed } = useUpsaleWarning();
	const handleClick = shouldRedirect
		? toggleWarningIfOrderConfirmed
		: servicePageProps.toggleModal.bind(null, ServiceType.Meal);
	const { t } = useTranslation('Checkout');
	const minPrice = aggregationMode ? minPriceProps : useSelector(getMealMinPrice);
	const issuedPrice = aggregationMode ? totalPriceProps : useSelector(getMealsPrice);
	const mealServices = useSelector(getMealServices);

	const { CheckoutAdditionalServiceMeal: css } = useTheme('CheckoutAdditionalService');

	const { onDeleteItem } = useCart();
	const isCanModifyServices = useIsCanModifyServices(mealServices);

	const buttons: ServiceButton[] = mealServices.length
		? [
				{
					text: isCanModifyServices ? t('Change') : t('More details'),
					action: servicePageProps.toggleModal.bind(null, ServiceType.Meal),
					buttonType: 'change'
				}
		  ]
		: [];

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	return (
		<>
			<ServiceSummary
				serviceType="Meal"
				onDelete={onDeleteItem}
				segmentId={segmentId}
			>
				{content => (
					<ServiceWrapper
						className={cn(css.meal, className)}
						icon={icons.mealIcon}
						addClick={showWarning ? toggleWarningIfOrderConfirmed : handleClick}
						buttons={buttons}
						header={t('Meal')}
						priceFrom={minPrice}
						description={t('Feel the taste in flight!')}
						coverClassName={css.meal_aggregation}
						mode={aggregationMode ? 'aggregator' : 'default'}
						isSelected={!!content}
						aggregationIcons={aggregationIcons}
						priceTotal={issuedPrice.amount ? issuedPrice : null}
						addButtonText={!isCanModifyServices ? t('More details') : null}
					>
						{content}
					</ServiceWrapper>
				)}
			</ServiceSummary>

			<Warning open={showWarning} redirectURL={servicesRedirectURL} onClose={closeWarning} />

			<ServicePage>
				{childrenProps => (
					<ServiceCardDialog
						order={servicePageProps.order}
						openedModal={servicePageProps.openedModal}
						onClose={servicePageProps.toggleModal.bind(null, null)}
						passengers={servicePageProps.passengers}
						passengerLabels={servicePageProps.passengerLabels}
						analyticsMeta={servicePageProps.analyticsMeta}
						segmentId={parseInt(segmentId)}
						{...childrenProps}
					/>
				)}
			</ServicePage>
		</>
	);
};

export default MealAdditionalService;
